// Create Work Order — shared modal.
//
// Used by both the Work Orders bucket page ("+ Submit new Work Order")
// and the Maintenance Queue page ("+ Create Work Order") so there is
// exactly one create form. Loads its own lookups (trucks / shops / users)
// on mount — it only mounts when opened, so no wasted requests.

import { useState, useEffect } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const INITIAL_FORM = {
  truck_id: '',
  title: '',
  description: '',
  priority: 'normal',
  assigned_to: '',
  assigned_shop_id: '',
  due_date: '',
  scheduled_date: ''
};

export default function CreateWorkOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [trucks, setTrucks] = useState([]);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getTrucks().then(t => setTrucks(Array.isArray(t) ? t : (t?.trucks || []))).catch(console.error);
    api.getShops().then(setShops).catch(console.error);
    api.getUsers().then(setUsers).catch(console.error);
  }, []);

  // Assignable users: maintenance team or driver team, active only
  const assignableUsers = users.filter(u =>
    (u.team_name === 'Truck Maintenance' || u.team_name === 'Driver Operations' || u.role === 'maintenance' || u.role === 'driver') && u.is_active !== 0
  );

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.truck_id || !form.title.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        truck_id: Number(form.truck_id),
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
        assigned_shop_id: form.assigned_shop_id ? Number(form.assigned_shop_id) : null,
        due_date: form.due_date || null,
        scheduled_date: form.scheduled_date || null
      };
      await api.createWorkOrder(payload);
      onCreated?.();
    } catch (err) {
      alert(err.message || 'Failed to create work order');
    }
    setSubmitting(false);
  }

  return (
    <Modal title="Create Work Order" onClose={onClose}>
      <form onSubmit={handleCreate}>
        <div className="form-group">
          <label>Truck *</label>
          <select
            value={form.truck_id}
            onChange={e => setForm(f => ({ ...f, truck_id: e.target.value }))}
            required
          >
            <option value="">Select truck...</option>
            {trucks.map(t => (
              <option key={t.id} value={t.id}>{t.unit_number} — {t.make} {t.model}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Brief description of the issue"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Detailed description of the work needed..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Priority</label>
          <select
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
          >
            {PRIORITY_OPTIONS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Assign To</label>
          <select
            value={form.assigned_to}
            onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
          >
            <option value="">Unassigned</option>
            {assignableUsers.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}{u.position_name ? ` — ${u.position_name}` : ''}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Shop</label>
          <select
            value={form.assigned_shop_id}
            onChange={e => setForm(f => ({ ...f, assigned_shop_id: e.target.value }))}
          >
            <option value="">No shop selected</option>
            {shops.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Due Date</label>
          <input
            type="date"
            value={form.due_date}
            onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label>Scheduled Date</label>
          <input
            type="date"
            value={form.scheduled_date}
            onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-muted" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-gold" disabled={submitting}>
            {submitting ? 'Saving...' : 'Create Work Order'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
